import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';

/**
 * 항목9(b) — BO 주문상세 "상품 추가입력(product collection)" 입력값 조회.
 *
 * be-catalog admin read endpoint(AdminProductCollectionOrderController, 커밋 03bea05 canonical):
 *   GET `/admin/v1/product-collections/by-order/{orderId}`
 *   → CatalogApiResponse `{ success, data: ProductCollectionInputView, error, meta }`
 *   ProductCollectionInputView = { orderId, products: [{ productCode, productName, inputs: [{ label, value, masked }] }] }
 *
 * ⚠ catalog 고유 네임스페이스(`/admin/v1/product-collections/**`) 하위 경로 — `/admin/v1/orders/**` 는
 * order-service 라우트라, `by-order` 세그먼트로 order-service /orders 경로와 명확히 구분한다. catalog
 * 기존 라우트(gateway catalog-service-admin, no-rewrite)가 커버하므로 게이트웨이 추가 변경 불필요.
 *
 * 체크아웃 시점 캡처(product_collection_staging)를 orderId 기준으로 상품별 그룹으로 읽는다(order push 없이
 * 노출만 — Lead (b) 설계). 통관(useOrderFormFieldValues)·이벤트(인라인)와 별개 레이어를 별도 query 로 소비.
 *   - 상품 식별 = `productCode`(자체상품코드, 내부 UUID 미노출). 항목 식별 = `label`(fieldKey/fieldId 미노출).
 *   - `masked=true` 면 `value` 는 BE 가 마스킹한 표시값(원문 비노출).
 *   - 캡처 없는 주문은 products 빈 목록 → 섹션 미렌더.
 *
 * 경로가 generated OpenAPI 스펙에 미반영이라 adminFetch 로 직접 호출한다.
 * RBAC: SUPER_ADMIN/OPS_ADMIN/MD_MANAGER/CS_AGENT/AUDITOR(BE @PreAuthorize). 마스킹은 BE 가공.
 */
export interface ProductCollectionInputItem {
  readonly label: string;
  readonly value: string;
  readonly masked: boolean;
}

export interface ProductCollectionGroup {
  /** 상품코드(이관 상품은 고도몰 상품번호, 내부 UUID 아님). 조회 실패 시 빈 문자열. */
  readonly productCode: string;
  /** 자체상품코드(상품의 현재 값, catalog release-qa/v1.0.21). 미설정·구버전 응답이면 null/없음. */
  readonly selfProductCode?: string | null;
  /** 상품명(참고 표시). 조회 실패 시 빈 문자열. */
  readonly productName: string;
  readonly inputs: ReadonlyArray<ProductCollectionInputItem>;
}

export interface ProductCollectionInputs {
  readonly orderId: string;
  readonly products: ReadonlyArray<ProductCollectionGroup>;
}

interface InputsEnvelope {
  success: boolean;
  data?: {
    orderId?: string;
    products?: ProductCollectionGroup[] | null;
  } | null;
}

const pathFor = (orderId: string) => `/admin/v1/product-collections/by-order/${orderId}`;

export const productCollectionInputsQueryKey = (orderId: string) =>
  ['admin', 'product-collection-inputs', orderId] as const;

const fetchInputs = async (orderId: string): Promise<ProductCollectionInputs> => {
  const res = await adminFetch(pathFor(orderId));
  if (!res.ok) {
    throw new Error(`product collection inputs fetch failed: ${res.status}`);
  }
  const env = (await res.json()) as InputsEnvelope;
  return {
    orderId: env.data?.orderId ?? orderId,
    // null/누락 가드 — 캡처 없는 주문도 안전히 빈 그룹 목록.
    products: (env.data?.products ?? []).filter(
      (g): g is ProductCollectionGroup => g != null,
    ),
  };
};

export const useProductCollectionInputsQuery = (orderId: string | undefined) =>
  useQuery<ProductCollectionInputs>({
    queryKey: productCollectionInputsQueryKey(orderId ?? ''),
    queryFn: () => fetchInputs(orderId as string),
    enabled: Boolean(orderId),
  });

/**
 * QA-302 — 원문보기 결과를 화면 간 공유한다(주문상품 표 인라인 + 상품 추가입력 섹션).
 * 원문은 섹션의 [원문보기](확인 + 감사로그)로만 받고, 이 캐시에 둔다. 섹션이 사라지면(화면 이탈·주문 변경) 지운다.
 * 자동 조회하지 않는다(queryFn 없음 — setQueryData 로만 채운다).
 */
export const productCollectionRawQueryKey = (orderId: string) =>
  ['admin', 'product-collection-inputs-raw', orderId] as const;

export const useProductCollectionRawQuery = (orderId: string | undefined) =>
  useQuery<ReadonlyArray<ProductCollectionGroup> | null>({
    queryKey: productCollectionRawQueryKey(orderId ?? ''),
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
  });

/** 표시용 입력값 — 원문을 받았으면 원문, 아니면 BE 마스킹 값. */
export const useProductCollectionDisplay = (orderId: string | undefined) => {
  const masked = useProductCollectionInputsQuery(orderId);
  const raw = useProductCollectionRawQuery(orderId);
  const groups = (raw.data ?? masked.data?.products ?? []).filter((g) => g.inputs.length > 0);
  return { groups, isRaw: raw.data != null, isPending: masked.isPending };
};
